import { Card } from '@/components/ui/Card';
import type { MetricDirection } from '@/lib/scorecards/status';
import type { InsightsMetric } from '@/api/insights.api';

import { Sparkline } from './Sparkline';
import { deltaTone, formatMetricValue, STATUS_DOT_CLASS, STATUS_LABEL, STATUS_TEXT_CLASS } from './format';

/**
 * KpiCard — a headline KPI tile for one metric: title, current value
 * (unit-formatted), a status dot, goal, a direction-aware
 * week-over-week delta, attainment vs goal, and an embedded Sparkline.
 *
 * When the metric has no entry in the window (`current === null`) the
 * card renders a calm "No data" state rather than zeros. Status tokens
 * and the value formatter live in `./format` so this file only exports
 * components.
 */

const DELTA_TONE_CLASS: Record<'good' | 'bad' | 'neutral', string> = {
  good: 'text-kw-emerald',
  bad: 'text-kw-danger-text',
  neutral: 'text-kw-muted',
};

// ── Week-over-week delta pill ────────────────────────────────────────────

export function WowDelta({ deltaPct, direction }: { deltaPct: number | null; direction: MetricDirection }) {
  if (deltaPct === null) {
    return <span className="text-[12px] text-kw-ghost">—</span>;
  }
  const tone = deltaTone(deltaPct, direction);
  const arrow = deltaPct > 0 ? '▲' : deltaPct < 0 ? '▼' : '■';
  const magnitude = Math.abs(deltaPct);
  return (
    <span className={`inline-flex items-center gap-1 text-[12px] font-medium ${DELTA_TONE_CLASS[tone]}`}>
      <span aria-hidden>{arrow}</span>
      {magnitude}% WoW
    </span>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────

export function KpiCard({ metric }: { metric: InsightsMetric }) {
  const { current } = metric;
  const seriesValues = metric.series.map((p) => p.value);

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-[13px] font-medium text-kw-shark" title={metric.title}>
            {metric.title}
          </p>
          <p className="mt-0.5 text-[11px] text-kw-muted">
            Goal {formatMetricValue(metric.goal, metric)}
            {metric.owner ? ` · ${metric.owner.name}` : ''}
          </p>
        </div>
        {current ? (
          <span
            className={`mt-1 inline-block size-2.5 shrink-0 rounded-full ${STATUS_DOT_CLASS[current.status]}`}
            aria-label={STATUS_LABEL[current.status]}
            title={STATUS_LABEL[current.status]}
          />
        ) : null}
      </div>

      {current ? (
        <>
          <div className="flex items-baseline gap-2">
            <span className="text-[24px] font-semibold leading-none text-kw-shark">
              {formatMetricValue(current.value, metric)}
            </span>
            {metric.attainmentPct !== null ? (
              <span className="text-[12px] text-kw-muted">{metric.attainmentPct}% of goal</span>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-2">
            <WowDelta deltaPct={metric.wowDeltaPct} direction={metric.direction} />
            <div className={`h-6 w-24 ${STATUS_TEXT_CLASS[current.status]}`}>
              <Sparkline values={seriesValues} reversed height={24} aria-label={`${metric.title} trend`} />
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] text-kw-ghost">No data yet</span>
          <div className="h-6 w-24 text-kw-ghost">
            <Sparkline values={seriesValues} reversed height={24} aria-label={`${metric.title} trend`} />
          </div>
        </div>
      )}
    </Card>
  );
}
